import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Card, CardActionArea, CardContent, CircularProgress, Typography } from '@mui/material';
import RadioIcon from '@mui/icons-material/Radio';
import PauseIcon from '@mui/icons-material/Pause';
import { useTranslation } from 'react-i18next';
import useHoverNavigation from '../use-hover-navigation';
import { useHoverNavigationContext } from '../hover-navigation-context';
import { hoverHighlightSx } from '../hover-style';
import AppsActionBarWithSettings from '../apps-action-bar-with-settings';

// Radio France stations with their stream URLs
const RADIO_STATIONS = [
    {
        id: 'france-inter',
        nameKey: 'APPLICATIONS.RADIO.FRANCE_INTER',
        streamUrl: 'https://icecast.radiofrance.fr/franceinter-midfi.mp3',
        color: '#e2001a',
    },
    {
        id: 'le-mouv',
        nameKey: 'APPLICATIONS.RADIO.LE_MOUV',
        streamUrl: 'https://icecast.radiofrance.fr/mouv-midfi.mp3',
        color: '#ff6600',
    },
    {
        id: 'fip',
        nameKey: 'APPLICATIONS.RADIO.FIP',
        streamUrl: 'https://icecast.radiofrance.fr/fip-midfi.mp3',
        color: '#ff1e8c',
    },
    {
        id: 'france-musique',
        nameKey: 'APPLICATIONS.RADIO.FRANCE_MUSIQUE',
        streamUrl: 'https://icecast.radiofrance.fr/francemusique-midfi.mp3',
        color: '#8250c4',
    },
    {
        id: 'france-info',
        nameKey: 'APPLICATIONS.RADIO.FRANCE_INFO',
        streamUrl: 'https://icecast.radiofrance.fr/franceinfo-midfi.mp3',
        color: '#0056a3',
    },
    {
        id: 'france-culture',
        nameKey: 'APPLICATIONS.RADIO.FRANCE_CULTURE',
        streamUrl: 'https://icecast.radiofrance.fr/franceculture-midfi.mp3',
        color: '#9c27b0',
    },
];

export default function Radio() {
    const { t } = useTranslation();
    const { active: navigationActive, toggle: toggleNavigation } = useHoverNavigationContext();
    const [actionBarActions, setActionBarActions] = useState([]);
    const [playingStation, setPlayingStation] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const audioRef = useRef(null);

    const itemCount = RADIO_STATIONS.length + actionBarActions.length;

    // Custom action: Pause button (only visible when playing)
    const customActions = useMemo(() => {
        if (!playingStation) return [];
        return [{
            icon: PauseIcon,
            label: t('APPLICATIONS.RADIO.PAUSE'),
            onClick: () => {
                if (audioRef.current) {
                    audioRef.current.pause();
                    audioRef.current.src = '';
                }
                setPlayingStation(null);
                setIsLoading(false);
            },
        }];
    }, [playingStation, t]);

    const onSelect = useCallback((index) => {
        if (index < RADIO_STATIONS.length) {
            // Station card clicked
            const station = RADIO_STATIONS[index];
            setIsLoading(true);
            setPlayingStation(station);

            // Pause navigation when starting a station
            if (navigationActive) {
                toggleNavigation();
            }

            if (audioRef.current) {
                audioRef.current.src = station.streamUrl;
                audioRef.current.play().catch((error) => {
                    console.error('Error playing audio:', error);
                    setIsLoading(false);
                    setPlayingStation(null);
                });
            }
        } else {
            // Action bar item clicked
            const actionIndex = index - RADIO_STATIONS.length;
            if (actionIndex >= 0 && actionIndex < actionBarActions.length) {
                actionBarActions[actionIndex].onClick();
            }
        }
    }, [actionBarActions, navigationActive, toggleNavigation]);

    const { active, activeIndex } = useHoverNavigation(itemCount, onSelect);

    // Initialize audio element
    useEffect(() => {
        audioRef.current = new Audio();
        audioRef.current.addEventListener('playing', () => setIsLoading(false));
        audioRef.current.addEventListener('waiting', () => setIsLoading(true));
        audioRef.current.addEventListener('error', () => {
            setIsLoading(false);
            setPlayingStation(null);
        });

        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.src = '';
            }
        };
    }, []);

    return (
        <Box sx={{ p: 0, position: 'relative', minHeight: '100%' }}>
            {/* Action bar */}
            <AppsActionBarWithSettings
                customActions={customActions}
                helperActions={[]}
                activeIndex={activeIndex - RADIO_STATIONS.length}
                active={active && activeIndex >= RADIO_STATIONS.length}
                onActionsChange={setActionBarActions}
                flex="0 0 auto"
            />

            <Typography variant="h4" gutterBottom>
                {t('APPLICATIONS.RADIO.TITLE')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.disabled', mb: 3 }}>
                {t('APPLICATIONS.RADIO.SUBTITLE')}
            </Typography>

            {/* Station Grid */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 3, mb: 4 }}>
                {RADIO_STATIONS.map((station, index) => {
                    const isPlaying = playingStation?.id === station.id;
                    const isHovered = active && index === activeIndex;
                    const cardSx = {
                        transition: 'box-shadow 0.3s ease, transform 0.3s ease',
                        opacity: playingStation && !isPlaying ? 0.5 : 1,
                        ...(isHovered && hoverHighlightSx),
                    };

                    return (
                        <Card key={station.id} sx={cardSx}>
                            <CardActionArea onClick={() => onSelect(index)}>
                                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                                    <Box
                                        sx={{
                                            width: 64,
                                            height: 64,
                                            borderRadius: '50%',
                                            bgcolor: station.color,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mx: 'auto',
                                            mb: 2,
                                        }}
                                    >
                                        {isPlaying && isLoading ? (
                                            <CircularProgress size={32} sx={{ color: 'white' }} />
                                        ) : (
                                            <RadioIcon sx={{ fontSize: 36, color: 'white' }} />
                                        )}
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: isPlaying ? 700 : 400 }}>
                                        {t(station.nameKey)}
                                    </Typography>
                                    {isPlaying && (
                                        <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                                            {t('APPLICATIONS.RADIO.NOW_PLAYING')}
                                        </Typography>
                                    )}
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    );
                })}
            </Box>
        </Box>
    );
}
